// One slider row of the right pane, Photon's "standard" stem row
// (StemRowLayout.hpp kStandardRow, design node 6005:16026): a 12px title line
// (8px colour dot + label) over a 20px controls line (slider, value box, S, M),
// 12px gap to the next row. Denoise models use the same row in percent mode
// without S/M. The colour family (dot gradient, thumb, S/M fills) is derived
// from the stem colour exactly as the app does (ui/color.ts).
import { html, nothing } from 'lit';
import type { TemplateResult } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import type { Hex } from '@/state/models';
import { DB_MAX, DB_MIN, DB_STEP, formatDb } from '@/state/gain';
import { stemPalette } from './color';

export type RowMode = 'dB' | 'percent';

export interface RowProps {
  id: string;             // stem name; element ids derive from it
  title: string;
  color: Hex;
  mode: RowMode;
  value: number;          // dB in dB mode, 0..100 in percent mode
  dimmed: boolean;        // muted, or another stem is soloed (alpha 0.4)
  disabled: boolean;
  sm?: { soloed: boolean; muted: boolean };   // present on demix rows only
}

export interface RowCallbacks {
  onInput: (value: number) => void;          // slider drag, in the row's unit
  onCommitText: (text: string) => string;    // value box edit; returns the canonical text
  onReset: () => void;                       // double-click on the slider
  onSolo?: () => void;
  onMute?: () => void;
}

function rowRange(mode: RowMode): { min: number; max: number; step: number } {
  return mode === 'dB'
    ? { min: DB_MIN, max: DB_MAX, step: DB_STEP }
    : { min: 0, max: 100, step: 1 };
}

export function valueText(mode: RowMode, value: number): string {
  return mode === 'dB' ? formatDb(value) : String(Math.round(value));
}

export function renderStemRow(p: RowProps, cb: RowCallbacks): TemplateResult {
  const pal = stemPalette(p.color);
  const { min, max, step } = rowRange(p.mode);
  const fill = ((p.value - min) / (max - min)) * 100;
  const sliderId = `slider-${p.id}`;
  const text = valueText(p.mode, p.value);

  const commit = (e: Event) => {
    const input = e.target as HTMLInputElement;
    input.value = cb.onCommitText(input.value);
  };

  return html`
    <div
      class=${classMap({ 'stem-row': true, dimmed: p.dimmed, percent: p.mode === 'percent' })}
      style=${styleMap({
        '--stem': pal.main,
        '--stem-top': pal.gradTop,
        '--thumb-fill': pal.thumbFill,
        '--thumb-ring': pal.thumbRing,
        '--sm-fill': pal.smFill,
        '--sm-fill-hover': pal.smFillHover,
        '--sm-fill-pressed': pal.smFillPressed,
        '--fill': `${fill}%`,
      })}
    >
      <div class="stem-title">
        <span class="stem-dot" aria-hidden="true"></span>
        <label for=${sliderId}>${p.title}</label>
      </div>
      <div class="stem-controls">
        <input
          id=${sliderId}
          class="slider"
          type="range"
          min=${min}
          max=${max}
          step=${step}
          .value=${String(p.value)}
          aria-valuemin=${min}
          aria-valuemax=${max}
          aria-valuenow=${p.value}
          aria-valuetext=${p.mode === 'dB' ? text : `${text} percent`}
          ?disabled=${p.disabled}
          @input=${(e: Event) => cb.onInput(Number((e.target as HTMLInputElement).value))}
          @dblclick=${cb.onReset}
        />
        <input
          class="value-box"
          type="text"
          inputmode="decimal"
          autocomplete="off"
          .value=${text}
          aria-label="${p.title} value"
          ?disabled=${p.disabled}
          @focus=${(e: Event) => (e.target as HTMLInputElement).select()}
          @change=${commit}
          @keydown=${(e: KeyboardEvent) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            if (e.key === 'Escape') {
              (e.target as HTMLInputElement).value = text;
              (e.target as HTMLInputElement).blur();
            }
          }}
        />
        ${p.sm
          ? html`
              <button
                class="sm-btn"
                type="button"
                aria-label="Solo ${p.title}"
                aria-pressed=${p.sm.soloed ? 'true' : 'false'}
                ?disabled=${p.disabled}
                @click=${cb.onSolo}
              >S</button>
              <button
                class="sm-btn"
                type="button"
                aria-label="Mute ${p.title}"
                aria-pressed=${p.sm.muted ? 'true' : 'false'}
                ?disabled=${p.disabled}
                @click=${cb.onMute}
              >M</button>
            `
          : nothing}
      </div>
    </div>
  `;
}
